<script setup>
// [AstraChat] Visões alternativas do listing de caixas: "Compacto" (linhas densas) e
// "Kanban" (colunas por status de conexão). O modo "Cards" (padrão) fica no Index.vue.
import { computed, onMounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import InboxRowItem from './InboxRowItem.vue';
import { loadInboxHealth, statusFor } from './useInboxHealth';

const props = defineProps({
  inboxes: { type: Array, default: () => [] },
  mode: { type: String, default: 'rows' }, // 'rows' | 'kanban'
  isAdmin: { type: Boolean, default: false },
  // [AstraChat] #155 — Conexões: null = herda isAdmin (compatibilidade)
  canView: { type: Boolean, default: null },
  canDelete: { type: Boolean, default: null },
});
const emit = defineEmits(['delete']);

const { accountId } = useAccount();
onMounted(() => loadInboxHealth(accountId.value));

// Colunas do kanban = grupos de status de conexão.
const COLUMNS = [
  { key: 'connected', label: 'Conectado', dot: 'bg-n-teal-9' },
  { key: 'connecting', label: 'Conectando', dot: 'bg-n-amber-9' },
  { key: 'disconnected', label: 'Desconectado', dot: 'bg-n-ruby-9' },
  { key: 'other', label: 'Outros', dot: 'bg-n-slate-8' },
];

const groupOf = inbox => statusFor(inbox)?.group || 'other';

const columns = computed(() =>
  COLUMNS.map(c => ({
    ...c,
    items: props.inboxes.filter(i => groupOf(i) === c.key),
  })).filter(c => c.items.length)
);
</script>

<template>
  <!-- Compacto: uma linha por caixa -->
  <div
    v-if="mode === 'rows'"
    class="border-t divide-y divide-n-weak border-n-weak"
  >
    <InboxRowItem
      v-for="inbox in inboxes"
      :key="inbox.id"
      :inbox="inbox"
      :is-admin="isAdmin"
      :can-view="canView"
      :can-delete="canDelete"
      @delete="emit('delete', $event)"
    />
  </div>

  <!-- Kanban: colunas por status de conexão -->
  <div v-else class="flex gap-4 pb-2 overflow-x-auto">
    <div
      v-for="col in columns"
      :key="col.key"
      class="flex flex-col border w-72 shrink-0 rounded-xl bg-n-alpha-1 border-n-weak"
    >
      <div class="flex items-center gap-2 px-3 py-2 border-b border-n-weak">
        <span class="rounded-full size-2" :class="col.dot" />
        <span class="text-sm font-medium text-n-slate-12">{{ col.label }}</span>
        <span class="ml-auto text-xs text-n-slate-10">{{
          col.items.length
        }}</span>
      </div>
      <div class="flex flex-col gap-1 p-2">
        <InboxRowItem
          v-for="inbox in col.items"
          :key="inbox.id"
          :inbox="inbox"
          :is-admin="isAdmin"
          :can-view="canView"
          :can-delete="canDelete"
          stacked
          class="bg-n-solid-1 border border-n-weak"
          @delete="emit('delete', $event)"
        />
      </div>
    </div>
  </div>
</template>
